'use client'

import type React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { ArrowDown, ArrowUp, ArrowUpDown, ChefHat, HardHat, Sparkles, UserCheck, Users, UtensilsCrossed } from 'lucide-react'
import type { VolunteerTableRow } from '@/features/volunteers/types/volunteers.type'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { cn, shortenName } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'

type SkillColumn = {
  key: string
  label: string
  field: string
  category: 'construction' | 'other' | 'cooking'
}

export type RegionalRosterStats = {
  constructionSkills: number
  otherSkills: number
  potentialTeamLeads: number
  cooking: number
  dietaryRestrictions: number
  males: number
  females: number
}

const SKILL_COLUMNS: SkillColumn[] = [
  { key: 'team-leader', label: 'Team Leader', field: 'experienceLeadingTeams', category: 'construction' },
  { key: 'manual-labor', label: 'Manual Labor', field: 'experienceManualLabor', category: 'construction' },
  { key: 'general-construction', label: 'Gen Const', field: 'experienceConstruction', category: 'construction' },
  { key: 'drywall', label: 'Drywall Finishing', field: 'experienceDrywallFinishing', category: 'construction' },
  { key: 'rough-carpentry', label: 'Rough Carpentry', field: 'experienceRoughCarpentry', category: 'construction' },
  { key: 'finish-carpentry', label: 'Finish Carpentry', field: 'experienceFinishCarpentry', category: 'construction' },
  { key: 'flooring', label: 'Flooring', field: 'experienceFlooring', category: 'construction' },
  { key: 'woodworking', label: 'Woodworking', field: 'experienceWoodWorking', category: 'construction' },
  { key: 'electrical', label: 'Electrical', field: 'experienceElectrical', category: 'construction' },
  { key: 'plumbing', label: 'Plumbing', field: 'experiencePlumbing', category: 'construction' },
  { key: 'hvac', label: 'HVAC', field: 'experienceHvac', category: 'construction' },
  { key: 'arborist', label: 'Tree Work', field: 'experienceArborist', category: 'construction' },
  { key: 'roofing', label: 'Roofing', field: 'experienceRoofing', category: 'construction' },
  { key: 'heavy-equipment', label: 'Heavy Equipment', field: 'experienceHeavyEquipment', category: 'construction' },
  { key: 'alt-ministries', label: 'Alt Ministries', field: 'experienceAltMinistries', category: 'other' },
  { key: 'data-management', label: 'Data Mgmt', field: 'experienceDataManagement', category: 'other' },
  { key: 'storytelling', label: 'Social Media', field: 'experienceStorytelling', category: 'other' },
  { key: 'cooking', label: 'Cooking', field: 'mealPreparationAbility', category: 'cooking' },
]

function getRating(user: VolunteerTableRow, field: string): number | null {
  const value =
    (user as unknown as Record<string, unknown>)[field] ??
    (user.volunteer as unknown as Record<string, unknown> | undefined)?.[field]
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function hasConstructionSkill(user: VolunteerTableRow): boolean {
  return SKILL_COLUMNS
    .filter((column) => column.category === 'construction')
    .some((column) => (getRating(user, column.field) ?? 0) >= 3)
}

function hasOtherSkill(user: VolunteerTableRow): boolean {
  if ((getRating(user, 'experienceManualLabor') ?? 0) >= 1) return true
  if ((getRating(user, 'experienceAltMinistries') ?? 0) >= 1) return true
  if (hasConstructionSkill(user)) return false
  const data = getRating(user, 'experienceDataManagement') ?? 0
  const story = getRating(user, 'experienceStorytelling') ?? 0
  const lowConstruction = SKILL_COLUMNS
    .filter((column) => column.category === 'construction')
    .some((column) => {
      const rating = getRating(user, column.field)
      return rating === 1 || rating === 2
    })
  return data >= 1 || story >= 1 || lowConstruction
}

function isCookingInterested(user: VolunteerTableRow): boolean {
  const raw = (user.mealPreparationAbility ?? user.volunteer?.mealPreparationAbility ?? '').toString().trim().toLowerCase()
  if (!raw || raw === 'no' || raw === 'prefer_not') return false
  if (raw === 'yes' || raw === 'willing' || raw === 'experienced') return true
  const n = Number(raw)
  return Number.isFinite(n) && n >= 3
}

function hasDietaryRestriction(user: VolunteerTableRow): boolean {
  const raw = (user.dietaryRestrictions ?? user.volunteer?.dietaryRestrictions ?? '').toString().trim()
  if (!raw) return false
  return !['na', 'n/a', 'none', 'no'].includes(raw.toLowerCase())
}

function genderShort(user: VolunteerTableRow): string {
  const gender = (user.gender ?? user.volunteer?.gender ?? '').toString().toLowerCase()
  if (gender.startsWith('m')) return 'M'
  if (gender.startsWith('f')) return 'F'
  return '—'
}

export function calculateRegionalRosterStats(data: VolunteerTableRow[]): RegionalRosterStats {
  return {
    constructionSkills: data.filter(hasConstructionSkill).length,
    otherSkills: data.filter(hasOtherSkill).length,
    potentialTeamLeads: data.filter((user) => (getRating(user, 'experienceLeadingTeams') ?? 0) >= 3).length,
    cooking: data.filter(isCookingInterested).length,
    dietaryRestrictions: data.filter(hasDietaryRestriction).length,
    males: data.filter((user) => genderShort(user) === 'M').length,
    females: data.filter((user) => genderShort(user) === 'F').length,
  }
}

function getAge(user: VolunteerTableRow): number | null {
  // Prefer server-computed age (not affected by DOB masking)
  if (user.age != null) return user.age
  // Fallback: client-side calculation from DOB
  if (!user.dateOfBirth) return null
  const dobStr = String(user.dateOfBirth)
  // Handle masked DOB format: **/**/YYYY (returned for leaders with tier2 access)
  const maskedMatch = dobStr.match(/^\*\*\/\*\*\/(\d{4})$/)
  if (maskedMatch) {
    return new Date().getFullYear() - parseInt(maskedMatch[1], 10)
  }
  const birthDate = new Date(dobStr)
  if (Number.isNaN(birthDate.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDelta = today.getMonth() - birthDate.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) age -= 1
  return age
}

function formatMoney(value: string | null | undefined): string {
  if (value == null || String(value).trim() === '') return '—'
  const n = Number(value)
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value)
}

function getNotes(user: VolunteerTableRow): string {
  const dietary = (user.dietaryRestrictions ?? user.volunteer?.dietaryRestrictions ?? '').toString().trim()
  const other = (user.volunteer?.skillsAndQualities ?? '').toString().trim()
  const parts: string[] = []
  if (dietary && !['na', 'n/a', 'none', 'no'].includes(dietary.toLowerCase())) parts.push(`Dietary: ${dietary}`)
  if (other) parts.push(`Other: ${other}`)
  return parts.join(' | ')
}

function getFlightDetails(user: VolunteerTableRow): string {
  return (user.volunteer?.jamaicaTripFlightDetails ?? '').toString().trim()
}

function getOrganization(user: VolunteerTableRow): string {
  return (user.organizationName ?? user.volunteer?.organizationName ?? '').toString().trim()
}

function parseMoneySortValue(value: string | null | undefined): number {
  if (value == null || String(value).trim() === '') return Number.NEGATIVE_INFINITY
  const n = Number(value)
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY
}

function lastActiveSortValue(user: VolunteerTableRow): number {
  if (!user.lastActiveAt) return Number.NEGATIVE_INFINITY
  const time = Date.parse(user.lastActiveAt)
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY
}

function compareRosterRows(a: VolunteerTableRow, b: VolunteerTableRow, sortColumn: string): number {
  const nameA = a.fullname ?? a.email ?? ''
  const nameB = b.fullname ?? b.email ?? ''
  const skillColumn = SKILL_COLUMNS.find((column) => column.key === sortColumn)

  if (skillColumn) {
    return (getRating(a, skillColumn.field) ?? -1) - (getRating(b, skillColumn.field) ?? -1)
  }

  switch (sortColumn) {
    case 'name':
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
    case 'organization':
      return getOrganization(a).localeCompare(getOrganization(b), undefined, { sensitivity: 'base' })
    case 'age':
      return (getAge(a) ?? -1) - (getAge(b) ?? -1)
    case 'mf':
      return genderShort(a).localeCompare(genderShort(b), undefined, { sensitivity: 'base' })
    case 'role':
      return (a.role?.name ?? '').localeCompare(b.role?.name ?? '', undefined, { sensitivity: 'base' })
    case 'notes':
      return getNotes(a).localeCompare(getNotes(b), undefined, { sensitivity: 'base' })
    case 'flightDetails':
      return getFlightDetails(a).localeCompare(getFlightDetails(b), undefined, { sensitivity: 'base' })
    case 'cost':
      return parseMoneySortValue(a.groupPrice) - parseMoneySortValue(b.groupPrice)
    case 'payment':
      return parseMoneySortValue(a.groupPaid) - parseMoneySortValue(b.groupPaid)
    case 'lastActive':
      return lastActiveSortValue(a) - lastActiveSortValue(b)
    default:
      return 0
  }
}

type ColumnSortButtonProps = {
  columnKey: string
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  onCycleSort: (columnKey: string) => void
  ariaLabelBase: string
}

function ColumnSortButton({
  columnKey,
  sortColumn,
  sortDirection,
  onCycleSort,
  ariaLabelBase,
}: ColumnSortButtonProps) {
  const active = sortColumn === columnKey
  const mode = !active ? 'none' : sortDirection === 'asc' ? 'asc' : 'desc'
  const ariaLabel =
    mode === 'none'
      ? `${ariaLabelBase}: sort descending`
      : mode === 'desc'
        ? `${ariaLabelBase}: sort ascending`
        : `${ariaLabelBase}: clear sort order`
  const Icon = mode === 'asc' ? ArrowUp : mode === 'desc' ? ArrowDown : ArrowUpDown

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0 p-0 print:hidden"
      aria-label={ariaLabel}
      onClick={() => onCycleSort(columnKey)}
    >
      <Icon className={cn('h-3.5 w-3.5', active ? 'text-primary' : 'text-muted-foreground')} />
    </Button>
  )
}

function SortableHeader({
  columnKey,
  label,
  sortColumn,
  sortDirection,
  onCycleSort,
  className,
}: {
  columnKey: string
  label: string
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  onCycleSort: (columnKey: string) => void
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-1 min-w-0 flex-wrap', className)}>
      <span>{label}</span>
      <ColumnSortButton
        columnKey={columnKey}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onCycleSort={onCycleSort}
        ariaLabelBase={label}
      />
    </div>
  )
}

function RosterStatCard({
  title,
  count,
  icon,
  tone,
}: {
  title: string
  count: string | number
  icon: React.ReactNode
  tone: 'green' | 'amber' | 'blue' | 'gray'
}) {
  const toneClass = {
    green: 'bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-950/25 dark:border-emerald-900/50',
    amber: 'bg-amber-50/70 border-amber-200/70 dark:bg-amber-950/25 dark:border-amber-900/50',
    blue: 'bg-sky-50/70 border-sky-200/70 dark:bg-sky-950/25 dark:border-sky-900/50',
    gray: 'bg-slate-50/70 border-slate-200/70 dark:bg-slate-900/25 dark:border-slate-800/60',
  }[tone]
  const iconClass = {
    green: 'bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    amber: 'bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    blue: 'bg-sky-100/80 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    gray: 'bg-slate-100/80 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300',
  }[tone]

  return (
    <div className={cn('rounded p-4 w-full flex justify-between items-center gap-5 border min-w-0', toneClass)}>
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-sm">{title}</p>
        <p className="text-lg font-semibold">{count}</p>
      </div>
      <div className={cn('rounded-full p-3 w-12 aspect-square flex items-center justify-center', iconClass)}>
        {icon}
      </div>
    </div>
  )
}

export function RegionalVolunteersRoster({
  data,
  isLoading,
  stats,
}: {
  data: VolunteerTableRow[]
  isLoading: boolean
  stats?: RegionalRosterStats
}) {
  const { regionSlug } = useRegion()
  const showFlightDetails = regionSlug?.toLowerCase() === 'jamaica'
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const handleSortColumn = useCallback((columnKey: string) => {
    if (sortColumn !== columnKey) {
      setSortColumn(columnKey)
      setSortDirection('desc')
      return
    }
    if (sortDirection === 'desc') {
      setSortDirection('asc')
      return
    }
    setSortColumn(null)
  }, [sortColumn, sortDirection])
  const sortedData = useMemo(() => {
    const list = [...data]
    list.sort((a, b) => {
      if (!sortColumn) return lastActiveSortValue(b) - lastActiveSortValue(a)
      const comparison = compareRosterRows(a, b, sortColumn)
      return sortDirection === 'asc' ? comparison : -comparison
    })
    return list
  }, [data, sortColumn, sortDirection])
  const visiblePageStats = calculateRegionalRosterStats(sortedData)
  const displayStats = stats ?? visiblePageStats
  const minWidth = showFlightDetails ? 'min-w-[3600px]' : 'min-w-[3200px]'

  if (isLoading) {
    return <div className="py-12 text-center text-muted-foreground">Loading regional roster...</div>
  }

  if (sortedData.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">No volunteers match this region.</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:gap-3 print:hidden">
        <RosterStatCard title="Construction Skills" count={displayStats.constructionSkills} icon={<HardHat className="h-5 w-5" />} tone="green" />
        <RosterStatCard title="Other Skills" count={displayStats.otherSkills} icon={<Sparkles className="h-5 w-5" />} tone="amber" />
        <RosterStatCard title="Potential Team Leads" count={displayStats.potentialTeamLeads} icon={<UserCheck className="h-5 w-5" />} tone="green" />
        <RosterStatCard title="Interested in cooking" count={displayStats.cooking} icon={<ChefHat className="h-5 w-5" />} tone="blue" />
        <RosterStatCard title="Dietary restrictions" count={displayStats.dietaryRestrictions} icon={<UtensilsCrossed className="h-5 w-5" />} tone="blue" />
        <RosterStatCard title="Males / Females" count={`${displayStats.males} / ${displayStats.females}`} icon={<Users className="h-5 w-5" />} tone="gray" />
      </div>

      <div className="relative max-h-[min(85vh,56rem)] w-full overflow-auto print:max-h-none print:overflow-visible">
        <Table noScrollWrapper className={cn(minWidth, 'print:min-w-0 print:w-full print:text-xs')}>
          <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-[25] [&_th]:shadow-[0_1px_0_0_hsl(var(--border))]">
            <TableRow>
              <TableHead className="bg-muted sticky left-0 z-[35]">
                <SortableHeader columnKey="name" label="Name" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} />
              </TableHead>
              <TableHead className="bg-muted">
                <SortableHeader columnKey="organization" label="Organization" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} />
              </TableHead>
              <TableHead className="bg-muted text-center">
                <SortableHeader columnKey="age" label="Age" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} className="justify-center" />
              </TableHead>
              <TableHead className="bg-muted text-center">
                <SortableHeader columnKey="mf" label="M/F" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} className="justify-center" />
              </TableHead>
              <TableHead className="bg-muted">
                <SortableHeader columnKey="role" label="Role" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} />
              </TableHead>
              {SKILL_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    'text-center',
                    column.category === 'construction' && 'bg-sky-100/80',
                    column.category === 'other' && 'bg-violet-100/70',
                    column.category === 'cooking' && 'bg-yellow-100/80'
                  )}
                >
                  <SortableHeader columnKey={column.key} label={column.label} sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} className="justify-center" />
                </TableHead>
              ))}
              <TableHead className="bg-muted">
                <SortableHeader columnKey="notes" label="Notes" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} />
              </TableHead>
              {showFlightDetails && (
                <TableHead className="bg-muted">
                  <SortableHeader columnKey="flightDetails" label="Flight Details" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} />
                </TableHead>
              )}
              <TableHead className="bg-muted text-right">
                <SortableHeader columnKey="cost" label="Cost" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} className="justify-end" />
              </TableHead>
              <TableHead className="bg-muted text-right">
                <SortableHeader columnKey="payment" label="Payment" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} className="justify-end" />
              </TableHead>
              <TableHead className="bg-muted">
                <SortableHeader columnKey="lastActive" label="Last Active" sortColumn={sortColumn} sortDirection={sortDirection} onCycleSort={handleSortColumn} />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((user) => {
              const displayName = user.fullname ?? user.email ?? 'Unknown'
              const nameHighlight = hasConstructionSkill(user)
                ? 'bg-emerald-50/70 dark:bg-emerald-950/25'
                : hasOtherSkill(user)
                  ? 'bg-amber-50/70 dark:bg-amber-950/25'
                  : 'bg-background'
              return (
                <TableRow key={user.id}>
                  <TableCell className={cn('sticky left-0 z-10', nameHighlight)}>
                    <div className="flex items-center gap-2">
                      <Avatar className="print:hidden">
                        <AvatarImage src={user.profilePicture ?? undefined} className="object-cover object-top" />
                        <AvatarFallback className="bg-muted text-sm">{shortenName(displayName)}</AvatarFallback>
                      </Avatar>
                      <span className="text-foreground font-medium">{displayName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[220px]">
                    <span className="line-clamp-1" title={getOrganization(user)}>
                      {getOrganization(user) || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-center tabular-nums">{getAge(user) ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground text-center">{genderShort(user)}</TableCell>
                  <TableCell className="text-muted-foreground">{user.role?.name ?? '—'}</TableCell>
                  {SKILL_COLUMNS.map((column) => (
                    <TableCell key={`${user.id}-${column.key}`} className="text-muted-foreground tabular-nums text-center">
                      {getRating(user, column.field) ?? '—'}
                    </TableCell>
                  ))}
                  <TableCell className="text-muted-foreground max-w-[260px]">
                    <span className="line-clamp-1" title={getNotes(user)}>{getNotes(user) || '—'}</span>
                  </TableCell>
                  {showFlightDetails && (
                    <TableCell className="text-muted-foreground max-w-[300px] whitespace-pre-line">
                      <span className="line-clamp-2" title={getFlightDetails(user)}>{getFlightDetails(user) || '—'}</span>
                    </TableCell>
                  )}
                  <TableCell className="text-muted-foreground text-right tabular-nums">{formatMoney(user.groupPrice)}</TableCell>
                  <TableCell className="text-muted-foreground text-right tabular-nums">{formatMoney(user.groupPaid)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.lastActiveAt ? format(new Date(user.lastActiveAt), 'dd-MMM-yyyy') : 'N/A'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
